import React, { useState, useEffect } from 'react';
import Icon from '../components/ui/Icon';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../utils/authUtil';
import { usePopup } from '../context/PopupContext';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import useFormDraft from '../hooks/useFormDraft';
import './AddStudent.css';

const EMPTY_FORM = {
  name: '', email: '', password: '',
  roll_number: '', branch: '', phone: '',
  cgpa: '', arrears: '', year_of_study: '',
  placement_batch_year: new Date().getFullYear().toString(),
  college_id: ''
};

// Bulk import without a password column generates a unique temporary password
// per student and returns the list ONCE. There is no shared default password
// any more, so the admin must be given these to pass on to the students: save
// them straight away as a CSV. (Cells starting with = + - @ are prefixed so a
// spreadsheet never treats an imported value as a formula.)
const downloadCredentials = (credentials) => {
  const safe = (v) => {
    const s = String(v ?? '');
    return /^[=+\-@]/.test(s) ? "'" + s : s;
  };
  const csv = ['email,roll_number,temporary_password',
    ...credentials.map(c => [c.email, c.roll_number, c.temporary_password].map(v => '"' + safe(v).replace(/"/g, '""') + '"').join(','))].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'student-temporary-passwords-' + new Date().toISOString().slice(0, 10) + '.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const AddStudent = () => {
  const navigate = useNavigate();
  const user = getUser();
  const isSuperAdmin = user?.realRole === 'super_admin' || user?.role === 'super_admin';

  const { showConfirm } = usePopup();
  const [activeTab, setActiveTab] = useState('single');
  const [form, setForm] = useState(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);
  const [colleges, setColleges] = useState([]);
  const [file, setFile] = useState(null);
  const [bulkCollegeId, setBulkCollegeId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [rowErrors, setRowErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  // audit AD-10: refreshing/closing the tab mid-form used to silently discard everything typed —
  // this only guards while there is actually something unsaved (single-student tab only; the bulk
  // tab holds a File object, which can't be persisted, so there's nothing to protect there).
  useUnsavedChangesGuard(dirty);
  const { initialDraft, savedAt, clearDraft } = useFormDraft('add_student', form, { enabled: dirty });

  const restoreDraft = () => {
    setForm(f => ({ ...f, ...initialDraft.data }));
    setDirty(true);
    setDraftBannerDismissed(true);
  };
  const discardDraft = () => {
    clearDraft();
    setDraftBannerDismissed(true);
  };
  const showDraftBanner = activeTab === 'single' && !!initialDraft && !draftBannerDismissed && !dirty;

  // Fetch colleges list for super_admin college selector
  useEffect(() => {
    if (isSuperAdmin) {
      API.get('/colleges')
        .then(res => setColleges(res.data || []))
        .catch(err => console.error('Failed to load colleges:', err));
    }
  }, [isSuperAdmin]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setDirty(true);
    setError(''); setSuccess(''); setRowErrors([]);
  };

  const handleCancel = async () => {
    if (dirty) {
      const ok = await showConfirm('Discard this student?', 'You have unsaved changes. Leaving now will discard them.');
      if (!ok) return;
    }
    clearDraft();
    navigate('/admin/students');
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (isSuperAdmin && !form.college_id) {
      setError('Please select a college before adding a student.');
      return;
    }
    setLoading(true); setError(''); setSuccess(''); setRowErrors([]);
    try {
      const payload = { ...form };
      if (!isSuperAdmin) delete payload.college_id;
      await API.post('/students', payload);
      setSuccess('Student added successfully!');
      clearDraft();
      setDirty(false);
      setDraftBannerDismissed(false);
      setForm({ ...EMPTY_FORM, college_id: form.college_id }); // keep college selected for next student
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add student');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select an Excel or CSV file'); return; }
    if (isSuperAdmin && !bulkCollegeId) {
      setError('Please select a college before uploading students.');
      return;
    }
    setLoading(true); setError(''); setSuccess(''); setRowErrors([]);
    const formData = new FormData();
    formData.append('file', file);
    if (isSuperAdmin && bulkCollegeId) {
      formData.append('college_id', bulkCollegeId);
    }
    try {
      const res = await API.post('/students/bulk', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(res.data.message);
      if (res.data.credentials && res.data.credentials.length > 0) {
        downloadCredentials(res.data.credentials);
        setSuccess(res.data.message + ' Temporary passwords for ' + res.data.credentials.length +
          ' student(s) were downloaded as a CSV — share them securely; they cannot be shown again. Students can change them from their account.');
      }
      if (res.data.errors && res.data.errors.length > 0) {
        setRowErrors(res.data.errors);
      }
      setFile(null);
      if (document.getElementById('fileInput')) document.getElementById('fileInput').value = '';
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const CollegeSelector = ({ value, onChange, style }) => (
    <div className="form-group" style={{ marginBottom: '20px' }}>
      <label style={{ fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon name="building" size={16} /> Select College <span style={{ color: '#dc2626' }}>*</span>
      </label>
      <select
        name="college_id"
        value={value}
        onChange={onChange}
        required
        style={{
          width: '100%', padding: '11px 14px', borderRadius: 10,
          border: '2px solid #dc2626', fontSize: 14, fontWeight: 600,
          background: 'var(--z-danger-tint)', cursor: 'pointer', outline: 'none',
          ...style
        }}
      >
        <option value="">— Choose a college to add students to —</option>
        {colleges.map(c => (
          <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
        ))}
      </select>
    </div>
  );

  return (
    <>
      <div className="add-student-page-content">
        <div className="top-bar">
          <div>
            <h2>Add Student</h2>
            <p>Add single student or bulk upload via Excel</p>
          </div>
          <button className="back-btn" onClick={handleCancel}>
            <Icon name="chevron-left" size={16} /> Back to Students
          </button>
        </div>

        <div className="add-card">
          <div className="tab-switcher">
            <button className={activeTab === 'single' ? 'tab active' : 'tab'}
              onClick={() => { setActiveTab('single'); setError(''); setSuccess(''); setRowErrors([]); }}>
              Single Student
            </button>
            <button className={activeTab === 'bulk' ? 'tab active' : 'tab'}
              onClick={() => { setActiveTab('bulk'); setError(''); setSuccess(''); setRowErrors([]); }}>
              Bulk Upload
            </button>
          </div>

          {showDraftBanner && (
            <div className="alert warning" role="status">
              <div style={{ fontWeight: 'bold' }}>You have an unsaved student from {new Date(initialDraft.savedAt).toLocaleString()}.</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 10 }}>
                <button type="button" className="btn-submit" onClick={restoreDraft}>Restore draft</button>
                <button type="button" className="btn-cancel" onClick={discardDraft}>Discard</button>
              </div>
            </div>
          )}

          {error && <div className="alert error">{error}</div>}

          {(success || rowErrors.length > 0) && (
            <div className={`alert ${rowErrors.length === 0 ? 'success' : 'warning'}`}>
              <div style={{ fontWeight: 'bold' }}>{success}</div>
              {rowErrors.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '0.85em' }}>
                  <div style={{ color: '#d97706', marginBottom: '5px' }}>Top Reasons for Failure:</div>
                  <ul style={{ maxHeight: '150px', overflowY: 'auto', paddingLeft: '20px' }}>
                    {rowErrors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>
                        Row {err.row}: {err.email ? `(${err.email}) ` : ''} <strong>{err.error}</strong>
                      </li>
                    ))}
                    {rowErrors.length > 10 && <li>...and {rowErrors.length - 10} more errors.</li>}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeTab === 'single' ? (
            <form onSubmit={handleSingleSubmit}>
              {/* Super Admin: College Selector */}
              {isSuperAdmin && (
                <CollegeSelector
                  value={form.college_id}
                  onChange={handleChange}
                />
              )}

              <div className="form-grid">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input name="name" placeholder="Enter full name"
                    value={form.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Roll Number</label>
                  <input name="roll_number" placeholder="e.g. 21CS001"
                    value={form.roll_number} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" name="email" placeholder="student@email.com"
                    value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input type="password" name="password" placeholder="Set login password"
                    value={form.password} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>Branch</label>
                  <select name="branch" value={form.branch} onChange={handleChange}>
                    <option value="">Select branch</option>
                    <option value="CSE">CSE</option>
                    <option value="ECE">ECE</option>
                    <option value="EEE">EEE</option>
                    <option value="IT">IT</option>
                    <option value="MECH">MECH</option>
                    <option value="CIVIL">CIVIL</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input name="phone" placeholder="Enter phone number"
                    value={form.phone} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>CGPA</label>
                  <input type="number" step="0.01" name="cgpa" placeholder="e.g. 8.5"
                    value={form.cgpa} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Arrears</label>
                  <input type="number" name="arrears" placeholder="0"
                    value={form.arrears} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Year of Study</label>
                  <select name="year_of_study" value={form.year_of_study} onChange={handleChange}>
                    <option value="">Select year</option>
                    <option value="1">1st Year</option>
                    <option value="2">2nd Year</option>
                    <option value="3">3rd Year</option>
                    <option value="4">4th Year</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Placement Batch Year</label>
                  <input name="placement_batch_year" placeholder="e.g. 2026"
                    value={form.placement_batch_year} onChange={handleChange} />
                </div>
              </div>

              <div className="form-actions">
                {savedAt && <span style={{ fontSize: '0.8em', color: 'var(--z-muted)', alignSelf: 'center', marginRight: 'auto' }}>Draft autosaved {new Date(savedAt).toLocaleTimeString()}</span>}
                <button type="button" className="btn-cancel" onClick={handleCancel}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Student'}
                </button>
              </div>
            </form>
          ) : (
            <div className="bulk-section">
              {/* Super Admin: College Selector for bulk */}
              {isSuperAdmin && (
                <CollegeSelector
                  value={bulkCollegeId}
                  onChange={e => setBulkCollegeId(e.target.value)}
                />
              )}

              <div className="template-info">
                <div className="info-icon">i</div>
                <div>
                  <div className="info-title">Excel/CSV file format</div>
                  <div className="info-desc">Your file must have these column headers exactly:</div>
                  <div className="col-tags">
                    {['name','email','password','roll_number','branch','phone','cgpa','arrears','year_of_study','placement_batch_year'].map(c => (
                      <span key={c} className="col-tag">{c}</span>
                    ))}
                  </div>
                </div>
              </div>

              <form onSubmit={handleBulkSubmit}>
                <div className="file-upload-area"
                  onClick={() => document.getElementById('fileInput').click()}>
                  <div className="upload-icon"><Icon name="plus" size={28} /></div>
                  <div className="upload-text">
                    {file ? file.name : 'Click to select Excel or CSV file'}
                  </div>
                  <div className="upload-sub">
                    {file ? `${(file.size / 1024).toFixed(1)} KB` : 'Supports .xlsx, .xls, .csv'}
                  </div>
                  <input
                    id="fileInput" type="file"
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={(e) => { setFile(e.target.files[0]); setError(''); setSuccess(''); }}
                  />
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-cancel"
                    onClick={() => { setFile(null); document.getElementById('fileInput').value = ''; }}>
                    Clear
                  </button>
                  <button type="submit" className="btn-submit" disabled={loading || !file}>
                    {loading ? 'Uploading...' : 'Upload Students'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AddStudent;